import { interpolate, spring } from 'remotion'
import {
  blurInLineMotion,
  blurInMotion,
  driftMotion,
  dropMotion,
  elasticMotion,
  flickerMotion,
  flipInMotion,
  glitchMotion,
  glowMotion,
  jumpMotion,
  karaokeFillProgress,
  pulseMotion,
  punchMotion,
  riseMotion,
  scaleUpMotion,
  scaleUpPushMotion,
  shakeMotion,
  skewMotion,
  slamMotion,
  slideUpMotion,
  squashMotion,
  swingMotion,
  underlineMotion,
  waveMotion,
  wiggleMotion,
  type HighlightWordMotion,
  type HighlightWordWindow,
} from '@/lib/subtitles/highlightWordMotion'

// Type definitions
// Spoken/upcoming word styles also receive the current-word highlight style
// (null when highlight is off), for animations that keep spoken words lit.
type NeighborWordStyle = (
  baseStyle: any,
  word?: any,
  frame?: number,
  fps?: number,
  highlightStyle?: any,
) => any

interface Animation {
  name: string
  highlightStyle?: (baseStyle: any, word: any, frame: number, fps: number) => any
  highlightStyleBefore?: NeighborWordStyle
  highlightStyleAfter?: NeighborWordStyle
  /**
   * 0..1 share of the current word painted in the highlight color.
   * SubtitleWord draws it as a clipped copy over the line-colored word.
   */
  highlightFill?: (word: any, frame: number, fps: number) => number
  /** Style for the current word's text alone, without its surrounding spaces. */
  highlightTextStyle?: (word: any, frame: number, fps: number) => any
  lineStyle?: (baseStyle: any, line: any, frame: number, fps: number) => any
}

interface Word {
  startFrame: number
  endFrame: number
}

interface Line {
  startFrame: number
  endFrame: number
}

const keepNeighborsVisible = {
  highlightStyleBefore: (baseStyle: any) => ({
    ...baseStyle,
    opacity: 1,
  }),
  highlightStyleAfter: (baseStyle: any) => ({
    ...baseStyle,
    opacity: 1,
  }),
}

function revealWord(
  name: string,
  motion: (frame: number, word: HighlightWordWindow) => HighlightWordMotion,
): Animation {
  return {
    name,
    highlightStyle: (baseStyle: any, word: Word, frame: number) => ({
      ...baseStyle,
      ...motion(frame, word),
    }),
    highlightStyleBefore: (baseStyle: any) => ({
      ...baseStyle,
      opacity: 1,
    }),
    highlightStyleAfter: (baseStyle: any) => ({
      ...baseStyle,
      opacity: 0,
    }),
  }
}

function karaokeHighlight(
  name: string,
  motion: (frame: number, word: HighlightWordWindow) => HighlightWordMotion,
): Animation {
  return {
    name,
    highlightStyle: (baseStyle: any, word: Word, frame: number) => ({
      ...baseStyle,
      ...motion(frame, word),
    }),
    ...keepNeighborsVisible,
  }
}

export const AVAILABLE_ANIMATIONS: Animation[] = [
  {
    name: 'Sem Animação',
    highlightStyle: (baseStyle: any) => baseStyle,
    highlightStyleBefore: (baseStyle: any) => baseStyle,
    highlightStyleAfter: (baseStyle: any) => baseStyle,
    lineStyle: (baseStyle: any) => baseStyle,
  },
  {
    name: 'Fade In',
    highlightStyle: (baseStyle: any, word: Word, frame: number) => ({
      ...baseStyle,
      opacity: interpolate(
        frame,
        [word.startFrame, Math.max(word.endFrame, word.startFrame + 1)],
        [0, 1],
        {
          extrapolateRight: 'clamp',
        },
      ),
    }),
    highlightStyleBefore: (baseStyle: any) => ({
      ...baseStyle,
      opacity: 1,
    }),
    highlightStyleAfter: (baseStyle: any) => ({
      ...baseStyle,
      opacity: 0,
    }),
  },
  {
    name: 'Fade In - Peek Ahead',
    highlightStyle: (baseStyle: any, word: Word, frame: number) => ({
      ...baseStyle,
      opacity: interpolate(
        frame,
        [word.startFrame, Math.max(word.endFrame, word.startFrame + 1)],
        [0.2, 1],
        {
          extrapolateRight: 'clamp',
        },
      ),
    }),
    highlightStyleBefore: (baseStyle: any) => ({
      ...baseStyle,
      opacity: 1,
    }),
    highlightStyleAfter: (baseStyle: any) => ({
      ...baseStyle,
      opacity: 0.2,
    }),
  },
  {
    name: 'Pop In',
    highlightStyle: (baseStyle: any, word: Word, frame: number, fps: number) => {
      const scale = spring({
        frame: frame - word.startFrame,
        fps,
        durationInFrames: Math.max(1, word.endFrame - word.startFrame),
      })
      const opacity = interpolate(
        frame,
        [word.startFrame, Math.max(word.endFrame, word.startFrame + 1)],
        [0, 1],
        {
          extrapolateRight: 'clamp',
        },
      )

      return {
        ...baseStyle,
        transform: `scale(${scale})`,
        opacity,
      }
    },
    highlightStyleBefore: (baseStyle: any) => ({
      ...baseStyle,
    }),
    highlightStyleAfter: (baseStyle: any) => ({
      ...baseStyle,
      transform: 'scale(0)',
    }),
  },
  revealWord('Blur In', blurInMotion),
  revealWord('Slide Up', slideUpMotion),
  revealWord('Slam', slamMotion),
  revealWord('Flip In', flipInMotion),
  karaokeHighlight('Scale Up', scaleUpMotion),
  karaokeHighlight('Scale Up - Push', scaleUpPushMotion),
  karaokeHighlight('Punch', punchMotion),
  karaokeHighlight('Jump', jumpMotion),
  karaokeHighlight('Pulse', pulseMotion),
  karaokeHighlight('Rise', riseMotion),
  karaokeHighlight('Drop', dropMotion),
  karaokeHighlight('Swing', swingMotion),
  karaokeHighlight('Wiggle', wiggleMotion),
  karaokeHighlight('Squash', squashMotion),
  karaokeHighlight('Skew', skewMotion),
  karaokeHighlight('Drift', driftMotion),
  karaokeHighlight('Elastic', elasticMotion),
  karaokeHighlight('Wave', waveMotion),
  karaokeHighlight('Flicker', flickerMotion),
  {
    name: 'Karaoke Fill',
    // Keeps the highlight color: SubtitleWord moves it onto the fill copy,
    // and the curved (Círculo) word, which has no copy, shows it in full.
    highlightStyle: (baseStyle: any) => ({ ...baseStyle }),
    highlightFill: (word: Word, frame: number) => karaokeFillProgress(frame, word),
    highlightStyleBefore: (baseStyle: any, _word?: Word, _frame?: number, _fps?: number, highlightStyle?: any) => ({
      ...baseStyle,
      opacity: 1,
      ...(highlightStyle?.color ? { color: highlightStyle.color } : {}),
    }),
    highlightStyleAfter: (baseStyle: any) => ({
      ...baseStyle,
      opacity: 1,
    }),
  },
  karaokeHighlight('Glow', glowMotion),
  karaokeHighlight('Shake', shakeMotion),
  karaokeHighlight('Glitch', glitchMotion),
  {
    name: 'Underline',
    highlightStyle: (baseStyle: any) => ({ ...baseStyle }),
    highlightTextStyle: (word: Word, frame: number) => underlineMotion(frame, word),
    ...keepNeighborsVisible,
  },
  {
    name: 'Pop In - Whole',
    lineStyle: (baseStyle: any, line: Line, frame: number, fps: number) => {
      const MAX_DURATION_FRAMES = 15
      const endFrame = Math.min(
        Math.max(line.endFrame, line.startFrame + 1),
        line.startFrame + MAX_DURATION_FRAMES,
      )

      const scale = spring({
        frame: frame - line.startFrame,
        fps,
        durationInFrames: Math.max(1, endFrame - line.startFrame),
      })
      const opacity = interpolate(
        frame,
        [line.startFrame, endFrame],
        [0, 1],
        {
          extrapolateRight: 'clamp',
        },
      )

      return {
        ...baseStyle,
        transform: `scale(${scale})`,
        opacity,
      }
    },
  },
  {
    name: 'Pop In - Whole - Fall',
    lineStyle: (baseStyle: any, line: Line, frame: number, fps: number) => {
      const MAX_DURATION_FRAMES = 10
      const endFrame = Math.min(
        Math.max(line.endFrame, line.startFrame + 1),
        line.startFrame + MAX_DURATION_FRAMES,
      )

      const scale = spring({
        frame: frame - line.startFrame,
        fps,
        durationInFrames: Math.max(1, endFrame - line.startFrame),
      })
      const opacity = interpolate(
        frame,
        [line.startFrame, endFrame],
        [0, 1],
        {
          extrapolateRight: 'clamp',
        },
      )
      const initialFallFrame = endFrame + 20
      const currentFrame = frame >= initialFallFrame ? frame - initialFallFrame : 0
      const acceleration = 10
      const initialVelocity = -30
      const velocity = initialVelocity + currentFrame * (acceleration / 2)
      const y = velocity * currentFrame

      return {
        ...baseStyle,
        transform: `scale(${scale}) translateY(${y}px)`,
        opacity,
      }
    },
  },
  {
    name: 'Blur In - Whole',
    lineStyle: (baseStyle: any, line: Line, frame: number) => ({
      ...baseStyle,
      ...blurInLineMotion(frame, line),
    }),
  },
  {
    name: 'Fall In',
    highlightStyle: (baseStyle: any, word: Word, frame: number, fps: number) => {
      const PIXELS_TO_FALL = 100
      const y = spring({
        frame: frame - word.startFrame,
        fps,
        durationInFrames: Math.max(1, word.endFrame - word.startFrame),
      })
      const opacity = interpolate(
        frame,
        [word.startFrame, Math.max(word.endFrame, word.startFrame + 1)],
        [0, 1],
        {
          extrapolateRight: 'clamp',
        },
      )

      return {
        ...baseStyle,
        transform: `translateY(${(y * PIXELS_TO_FALL) - PIXELS_TO_FALL}px)`,
        opacity,
      }
    },
    highlightStyleBefore: (baseStyle: any) => ({
      ...baseStyle,
      opacity: 1,
    }),
    highlightStyleAfter: (baseStyle: any) => ({
      ...baseStyle,
      opacity: 0,
    }),
  },
  {
    name: 'Fall In - Whole',
    lineStyle: (baseStyle: any, line: Line, frame: number, fps: number) => {
      const MAX_DURATION_FRAMES = 30
      const endFrame = Math.min(
        Math.max(line.endFrame, line.startFrame + 1),
        line.startFrame + MAX_DURATION_FRAMES,
      )
      const PIXELS_TO_FALL = 100
      const y = spring({
        frame: frame - line.startFrame,
        fps,
        durationInFrames: Math.max(1, endFrame - line.startFrame),
      })
      const opacity = interpolate(
        frame,
        [line.startFrame, endFrame],
        [0, 1],
        {
          extrapolateRight: 'clamp',
        },
      )

      return {
        ...baseStyle,
        transform: `translateY(${(y * PIXELS_TO_FALL) - PIXELS_TO_FALL}px)`,
        opacity,
      }
    },
  },
  {
    name: 'Roll In',
    highlightStyle: (baseStyle: any, word: Word, frame: number, fps: number) => {
      const ANGLE_TO_ROLL = 60
      const r = spring({
        frame: frame - word.startFrame,
        fps,
        durationInFrames: Math.max(1, word.endFrame - word.startFrame),
      })
      const opacity = interpolate(
        frame,
        [word.startFrame, Math.max(word.endFrame, word.startFrame + 1)],
        [0, 1],
        {
          extrapolateRight: 'clamp',
        },
      )

      return {
        ...baseStyle,
        transform: `rotate(${ANGLE_TO_ROLL - (r * ANGLE_TO_ROLL)}deg)`,
        opacity,
      }
    },
    highlightStyleBefore: (baseStyle: any) => ({
      ...baseStyle,
      opacity: 1,
    }),
    highlightStyleAfter: (baseStyle: any) => ({
      ...baseStyle,
      opacity: 0,
    }),
  },
  {
    name: 'Roll In - Whole',
    lineStyle: (baseStyle: any, line: Line, frame: number, fps: number) => {
      const ANGLE_TO_ROLL = 60
      const MAX_DURATION_FRAMES = 15
      const endFrame = Math.min(
        Math.max(line.endFrame, line.startFrame + 1),
        line.startFrame + MAX_DURATION_FRAMES,
      )
      const r = spring({
        frame: frame - line.startFrame,
        fps,
        durationInFrames: Math.max(1, endFrame - line.startFrame),
      })
      const opacity = interpolate(
        frame,
        [line.startFrame, endFrame],
        [0, 1],
        {
          extrapolateRight: 'clamp',
        },
      )

      return {
        ...baseStyle,
        transform: `rotate(${ANGLE_TO_ROLL - (r * ANGLE_TO_ROLL)}deg)`,
        opacity,
      }
    },
  },
  {
    name: 'Wave - Whole',
    lineStyle: (baseStyle: any, line: Line, frame: number) => {
      const elapsed = Math.max(0, frame - line.startFrame)
      const wave = Math.sin((elapsed / 32) * Math.PI * 2)
      return {
        ...baseStyle,
        transform: `translateY(${-8 * wave}px) rotate(${6 * wave}deg)`,
      }
    },
  },
  {
    name: 'Swing - Whole',
    lineStyle: (baseStyle: any, line: Line, frame: number, fps: number) => {
      const MAX_DURATION_FRAMES = 18
      const endFrame = Math.min(
        Math.max(line.endFrame, line.startFrame + 1),
        line.startFrame + MAX_DURATION_FRAMES,
      )
      const r = spring({
        frame: frame - line.startFrame,
        fps,
        durationInFrames: Math.max(1, endFrame - line.startFrame),
      })
      return {
        ...baseStyle,
        transform: `rotate(${-14 * (1 - r)}deg)`,
        transformOrigin: 'center top',
      }
    },
  },
]
